/**
 * Offline cache for GET responses — stores candidate profiles + lists so the
 * pre-recruiter can keep reading them when the network is down.
 *
 * Storage strategy:
 *   - `localStorage` for small entries (lists, single candidate without CV PDF)
 *   - Excludes `cv_pdf_data` to keep us under the 5 MB localStorage quota
 *   - TTL: 6 hours (matches our call-queue TTL)
 *
 * Used by `api.js`:
 *   - On every GET that succeeds, the response is cached under its full URL key.
 *   - On every GET that fails with a network error, we serve the cached value
 *     (if any) so the UI doesn't blow up.
 */
const PREFIX = "prerec.cache.";
const TTL_MS = 6 * 3600 * 1000;
const MAX_BYTES_PER_ENTRY = 1_500_000; // ~1.5 MB after stripping CV PDFs

function strip(value) {
    // Drop heavy fields that would bloat the cache (CV PDFs in base64).
    if (!value || typeof value !== "object") return value;
    if (Array.isArray(value)) return value.map(strip);
    if (value.candidate && typeof value.candidate === "object") {
        return { ...value, candidate: strip(value.candidate) };
    }
    const cleaned = { ...value };
    if (cleaned.cv_pdf_data) cleaned.cv_pdf_data = "__OFFLINE_STRIPPED__";
    return cleaned;
}

function keyFor(url, params) {
    let key = PREFIX + url;
    if (params && typeof params === "object" && Object.keys(params).length > 0) {
        const sorted = Object.keys(params).sort().map((k) => `${k}=${params[k]}`).join("&");
        key += `?${sorted}`;
    }
    return key;
}


/** Persist a GET response. Silent on quota errors. */
export function cacheGet(url, params, data) {
    if (!data) return;
    try {
        const payload = JSON.stringify({ at: Date.now(), data: strip(data) });
        if (payload.length > MAX_BYTES_PER_ENTRY) return;
        localStorage.setItem(keyFor(url, params), payload);
    } catch (_e) { /* quota — silently skip */ }
}

/** Retrieve a cached GET response. Returns null if missing or expired. */
export function getCached(url, params) {
    try {
        const raw = localStorage.getItem(keyFor(url, params));
        if (!raw) return null;
        const { at, data } = JSON.parse(raw);
        if (!at || (Date.now() - at) > TTL_MS) {
            localStorage.removeItem(keyFor(url, params));
            return null;
        }
        return data;
    } catch (_e) { return null; }
}

/** Manually warm the cache (used by Focus Mode to preload N candidates). */
export function warmCache(url, params, data) { cacheGet(url, params, data); }

/** Clear ALL cached entries — useful on logout. */
export function clearCache() {
    try {
        const keys = [];
        for (let i = 0; i < localStorage.length; i += 1) {
            const k = localStorage.key(i);
            if (k && k.startsWith(PREFIX)) keys.push(k);
        }
        keys.forEach((k) => localStorage.removeItem(k));
    } catch (_e) { /* ignore */ }
}
